export type LocationMatchCandidate = {
  id: string
  name: string
  address: string | null
}

export type LocationMatchResult = {
  locationId: string | null
  normalizedName: string | null
  strategy: 'exact' | 'fuzzy' | 'none'
}

export type EventLocationFields = {
  googleLocation?: string | null
  title?: string | null
  description?: string | null
  location?: string | null
}

const normalizeLocationText = (value: string | null | undefined): string => {
  if (!value) return ''
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const levenshteinRatio = (a: string, b: string): number => {
  if (!a.length && !b.length) return 1
  if (!a.length || !b.length) return 0
  const matrix: number[][] = Array.from({ length: a.length + 1 }, () =>
    Array.from({ length: b.length + 1 }, () => 0),
  )
  for (let i = 0; i <= a.length; i += 1) matrix[i]![0] = i
  for (let j = 0; j <= b.length; j += 1) matrix[0]![j] = j
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      matrix[i]![j] = Math.min(
        matrix[i - 1]![j]! + 1,
        matrix[i]![j - 1]! + 1,
        matrix[i - 1]![j - 1]! + cost,
      )
    }
  }
  const distance = matrix[a.length]![b.length]!
  return 1 - distance / Math.max(a.length, b.length)
}

const FUZZY_THRESHOLD = 0.85

/** Prefixos comuns em nomes de unidades — ignorados na comparação por palavras. */
const LOCATION_PREFIX_TOKENS = new Set([
  'upa',
  'pas',
  'pa',
  'ps',
  'pronto',
  'socorro',
  'atendimento',
  'unidade',
  'posto',
  'hospital',
  'clinica',
  'clínica',
])

/** Palavras de conteúdo do nome (sem siglas de unidade), mín. 3 caracteres. */
const contentLocationTokens = (normalized: string): string[] => {
  const tokens = normalized.split(' ').filter((token) => token.length >= 2)
  const content = tokens.filter((token) => token.length >= 3 && !LOCATION_PREFIX_TOKENS.has(token))
  if (content.length > 0) return content

  const withoutPrefix = tokens.filter((token) => !LOCATION_PREFIX_TOKENS.has(token))
  if (withoutPrefix.length > 0) return withoutPrefix

  return tokens
}

/** Todas as palavras relevantes de `source` aparecem em `haystack` (contains, case já normalizado). */
const allContentWordsContainedIn = (haystack: string, source: string): boolean => {
  const needles = contentLocationTokens(source)
  if (needles.length === 0 || !haystack) return false
  return needles.every((needle) => haystack.includes(needle))
}

const tokensMatchLocationNames = (textNorm: string, nameNorm: string): boolean => {
  const textTokens = contentLocationTokens(textNorm)
  const nameTokens = contentLocationTokens(nameNorm)
  if (textTokens.length === 0 || nameTokens.length === 0) return false

  const [smaller, larger] =
    textTokens.length <= nameTokens.length ? [textTokens, nameTokens] : [nameTokens, textTokens]
  return smaller.every((token) => larger.includes(token))
}

const fuzzyMatchNormalized = (textNorm: string, nameNorm: string): boolean => {
  if (!textNorm || !nameNorm) return false
  if (textNorm === nameNorm) return true
  if (textNorm.includes(nameNorm) || nameNorm.includes(textNorm)) return true
  if (levenshteinRatio(textNorm, nameNorm) >= FUZZY_THRESHOLD) return true
  if (allContentWordsContainedIn(textNorm, nameNorm)) return true
  if (allContentWordsContainedIn(nameNorm, textNorm)) return true
  return tokensMatchLocationNames(textNorm, nameNorm)
}

/** Junta campos do evento para busca por contains (título + descrição + local). */
export const combineEventLocationSearchText = (fields: EventLocationFields): string => {
  const parts = [fields.googleLocation, fields.location, fields.title, fields.description]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
  return parts.join(' ')
}

export function matchLocationId(
  locations: LocationMatchCandidate[],
  text: string | null | undefined,
): LocationMatchResult {
  const normalizedText = normalizeLocationText(text)
  if (!normalizedText) {
    return { locationId: null, normalizedName: null, strategy: 'none' }
  }

  for (const loc of locations) {
    const normalizedName = normalizeLocationText(loc.name)
    if (normalizedName && normalizedName === normalizedText) {
      return { locationId: loc.id, normalizedName: loc.name, strategy: 'exact' }
    }
  }

  for (const loc of locations) {
    const normalizedName = normalizeLocationText(loc.name)
    if (!normalizedName) continue
    if (fuzzyMatchNormalized(normalizedText, normalizedName)) {
      return { locationId: loc.id, normalizedName: loc.name, strategy: 'fuzzy' }
    }
    const normalizedAddress = normalizeLocationText(loc.address)
    if (normalizedAddress && normalizedText.includes(normalizedAddress)) {
      return { locationId: loc.id, normalizedName: loc.name, strategy: 'fuzzy' }
    }
  }

  return { locationId: null, normalizedName: null, strategy: 'none' }
}

/** Sugere local a partir de título/descrição/local quando o usuário ainda não vinculou um local. */
export function suggestLocationFromEventFields(
  locations: LocationMatchCandidate[],
  fields: { title?: string; description?: string; location?: string },
): LocationMatchResult {
  const googleLocation = fields.location?.trim() || null
  if (googleLocation) {
    const fromLocation = matchLocationId(locations, googleLocation)
    if (fromLocation.locationId) return fromLocation
  }
  const fromTitle = matchLocationId(locations, fields.title)
  if (fromTitle.locationId) return fromTitle
  const fromDescription = matchLocationId(locations, fields.description)
  if (fromDescription.locationId) return fromDescription

  const combined = combineEventLocationSearchText({
    location: fields.location,
    title: fields.title,
    description: fields.description,
  })
  return matchLocationId(locations, combined)
}

export function resolveGoogleEventLocationMatch(
  locations: LocationMatchCandidate[],
  params: { googleLocation?: string | null; title?: string; description?: string | null },
): LocationMatchResult {
  const googleLocation = params.googleLocation?.trim() || null
  if (googleLocation) {
    const fromGoogle = matchLocationId(locations, googleLocation)
    if (fromGoogle.locationId) return fromGoogle
  }
  const fromTitle = matchLocationId(locations, params.title)
  if (fromTitle.locationId) return fromTitle
  const fromDescription = matchLocationId(locations, params.description)
  if (fromDescription.locationId) return fromDescription

  const combined = combineEventLocationSearchText({
    googleLocation: params.googleLocation,
    title: params.title,
    description: params.description,
  })
  return matchLocationId(locations, combined)
}
